import {
  Box,
  Button,
  ComponentMultiStyleConfig,
  Icon,
  Spinner,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useCallback, useMemo, useState } from "react";
import { CMFileUploadItem } from "../../file-upload";
import { CMButtonFileUpload } from "../../form";
import { MdUpload } from "react-icons/md";
import { FileUploadItemContent } from "../../file-upload/types";
import {
  CMFormInputBaseTextArea,
  CMFormInputBaseTextAreaProps,
} from "../CMFormInputBaseTextArea/CMFormInputBaseTextArea";

export type CMFormInputBaseFileAttachableTextAreaProps = {
  /**
   * 添付ファイル
   */
  files: FileUploadItemContent[];

  /**
   * ファイルアップロードボタンのラベル
   */
  buttonUploadLabel: string;

  /**
   * アップロード中であることを通知するラベル
   */
  uploadingLabel?: string;

  /**
   * ファイルが追加されたときに、その追加ファイルを引数として呼び出される。
   */
  onAddFile: (file: File) => Promise<void>;

  /**
   * ファイルが削除されたときに、その削除ファイルを引数として呼び出される。
   */
  onDeleteFile: (fileId: string) => void;

  /**
   * ファイルが選択されたときに、そのファイルを引数として呼び出される。
   * @param fileId
   * @param fileName ファイル名
   */
  onDownloadFile?: (fileId: string, fileName: string) => void;
} & CMFormInputBaseTextAreaProps;

/**
 * FormInputFileAttachableTextAreaコンポーネント
 *
 * コメントやエラーメッセージの設定が可能。
 * 入力文字数に応じて自動でサイズを変更する。
 * 添付ファイルの追加が可能。
 */
export const CMFormInputBaseFileAttachableTextArea = ({
  minRows,
  maxRows,
  editMode = "editable",
  variant = "default",
  size = "md",
  resize = "vertical",
  placeholder,
  changedFrom = null,
  value = null,
  onChange,
  files,
  buttonUploadLabel,
  uploadingLabel,
  onAddFile,
  onDeleteFile,
  onDownloadFile,
  onBlur,
  sx,
  ...rest
}: CMFormInputBaseFileAttachableTextAreaProps) => {
  const [isUploading, setIsUploading] = useState(false);

  const styles = useMultiStyleConfig(
    "CMFormInputBaseFileAttachableTextAreaStyles",
    { variant: variant },
  );

  /**
   * ファイルアップロードイベント
   * @param addedFiles 添付ファイル
   */
  const handleAddFile = useCallback(
    async (addedFiles: File[]) => {
      setIsUploading(true);
      for (const file of addedFiles) {
        await onAddFile(file);
      }
      setIsUploading(false);
    },
    [onAddFile],
  );

  /**
   * 添付ファイル削除イベント
   */
  const handleDelete = useCallback(
    (_index: number, file: FileUploadItemContent) => {
      onDeleteFile?.(file.id);
    },
    [onDeleteFile],
  );

  const containerStyles = useMemo(
    () => ({
      ...styles.containerBase,
      ...(editMode === "readOnly" ? styles.containerReadOnly : undefined),
      ...sx,
    }),
    [styles.containerBase, styles.containerReadOnly, editMode, sx],
  );

  return (
    <Box sx={{ ...containerStyles, ...sx }} {...rest}>
      <Box __css={styles.formArea}>
        {/*テキストエリア*/}
        <CMFormInputBaseTextArea
          editMode={editMode}
          size={size}
          resize={resize}
          placeholder={placeholder}
          value={value}
          minRows={minRows}
          maxRows={maxRows}
          onChange={onChange}
          onBlur={onBlur}
          changedFrom={changedFrom}
          sx={styles.commentArea}
        />
        <Box sx={styles.fileArea}>
          {/*アップロード済みファイルエリア*/}
          <Box sx={styles.fileUploadedArea}>
            {files.map((file, index) => (
              <CMFileUploadItem
                key={index}
                editMode={editMode}
                fileItem={file}
                onDelete={() => handleDelete(index, file)}
                onDownload={onDownloadFile}
                sx={styles.file}
              />
            ))}
          </Box>

          {/*ファイルアップロードボタンエリア*/}
          {editMode === "editable" && (
            <CMButtonFileUpload
              onUpload={handleAddFile}
              accept={undefined}
              multiple={true}
              isUploading={isUploading}
            >
              {/*ファイル添付ボタン*/}
              <Button isDisabled={isUploading} sx={styles.uploadButton}>
                <Box sx={styles.uploadButtonContents}>
                  {isUploading ? (
                    <>
                      <Spinner sx={styles.uploadButtonIcon} />
                      <Text sx={styles.uploadButtonLabel}>
                        {uploadingLabel}
                      </Text>
                    </>
                  ) : (
                    <>
                      <Icon as={MdUpload} sx={styles.uploadButtonIcon} />
                      <Text sx={styles.uploadButtonLabel}>
                        {buttonUploadLabel}
                      </Text>
                    </>
                  )}
                </Box>
              </Button>
            </CMButtonFileUpload>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const CMFormInputBaseFileAttachableTextAreaStyles: ComponentMultiStyleConfig =
  {
    parts: [
      "containerBase",
      "formArea",
      "commentArea",
      "fileArea",
      "fileUploadedArea",
      "file",
      "uploadButton",
      "buttonContents",
      "buttonIcon",
      "buttonLabel",
    ],
    baseStyle: {
      containerBase: {
        display: "flex",
        flexDirection: "column",
      },
      formArea: {
        overflow: "hidden",
        outline: "2px solid transparent",
        outlineOffset: "2px",
        border: "solid 1px",
        borderColor: "gray.300",
        borderRadius: "lg",
        _focusWithin: {
          borderColor: "blue.500",
          boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
        },
      },
      commentArea: {
        w: "100%",
        minH: "100px",
        px: "4",
        py: "2",
        borderBottomRightRadius: "0px",
        borderBottomLeftRadius: "0px",
        border: "none",
        outline: "none",
        boxShadow: "none",
        color: "gray.700",
      },
      commentAreaOption: {
        _focus: {
          boxShadow: "none",
        },
      },
      fileArea: {
        border: "none",
        outline: "none",
        boxShadow: "none",
        bgColor: "gray.100",
      },
      fileUploadedArea: {
        bgColor: "gray.100",
      },
      file: {
        position: "relative",
        ml: "20px",
        paddingTop: "4px",
      },
      uploadButton: {
        h: "32px",
        w: "100%",
        borderTopRightRadius: "0px",
        borderTopLeftRadius: "0px",
        position: "relative",
        marginTop: "2px",
        paddingTop: "6px",
        bgColor: "gray.100",
        _disabled: {
          color: "gray.500",
        },
      },
      uploadButtonContents: {
        display: "flex",
        flexDirection: "row",
        position: "absolute",
        left: "24px",
        color: "blackAlpha.500",
      },
      uploadButtonIcon: {
        width: "15px",
        height: "15px",
      },
      uploadButtonLabel: {
        h: "20px",
        marginLeft: "5px",
        fontWeight: "400",
        fontSize: "14px",
        fontStyle: "normal",
        fontFamily: "'Inter'",
        color: "blackAlpha.500",
      },
    },
    variants: {
      error: {
        formArea: {
          borderColor: "red.500",
          boxShadow: "0 0 0 1px var(--chakra-colors-red-500)",
        },
      },
    },
  };
